import { StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { loadableReady } from '@loadable/component';
import type { OnError } from '@johnlewispartnership/wtr-content-component-library/dist/component-library/config/types';

import {
  CONTENT_DATA_ATTRIBUTE,
  PATH_DATA_ATTRIBUTE,
  WINDOW_DATA_PROPERTY_NAME,
} from 'constants/render';
import { CHUNK_LOADING_GLOBAL } from 'constants/loadable';
import { getApm } from 'shared/apm';
import App from 'renderer/App';

const onError: OnError = async (error, additionalData) => {
  const apm = await getApm('error-only');

  if (apm?.captureError) {
    apm.captureError(Object.assign(error, additionalData));
    return;
  }

  console.error('APM is not available on the window.', error);
};

export const renderClient = () => {
  window.dataLayer = window.dataLayer || [];

  const rootContainer = document.currentScript?.closest(`[${PATH_DATA_ATTRIBUTE}]`);
  const contentPath = rootContainer?.getAttribute(PATH_DATA_ATTRIBUTE);
  const contentRoot = rootContainer?.querySelector(`[${CONTENT_DATA_ATTRIBUTE}="${contentPath}"]`);

  if (!rootContainer || !contentRoot || !contentPath) {
    const error = new Error('Content Renderer: Root container or content root not found');
    return onError(error, {
      logger: 'ContentRenderer | client',
      section: `Hydration Error`,
    });
  }

  const { content, config } = window[WINDOW_DATA_PROPERTY_NAME];

  const { componentData, pageData, preloadedData = {} } = content?.[contentPath] || {};

  const isPage = pageData !== undefined;

  if (isPage) {
    getApm('full-instrumentation');
  }

  loadableReady(
    () =>
      hydrateRoot(
        contentRoot,
        <StrictMode>
          <App
            componentLibraryConfig={{ ...config, onError }}
            componentData={componentData || []}
            pageData={pageData}
            contentPath={contentPath}
            preloadedData={preloadedData}
          />
        </StrictMode>,
        { identifierPrefix: `wtr-cr-${contentPath}` },
      ),
    { namespace: contentPath, chunkLoadingGlobal: CHUNK_LOADING_GLOBAL },
  );
};
